import * as d3 from "d3";
import { random } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { TimeFrequency } from "@app/projects/enumerators";
import { TrendChartItemType } from "@app/velvet/enumerators";
import {
  BarData,
  Colors,
  LineData,
  LocalStorageRunConfigurationItem,
  VelvetBarGroup,
  VelvetColors,
  VelvetConfiguration,
  VelvetItem,
  VelvetLabel,
  VelvetOptions,
  VelvetRenderItem,
} from "@app/velvet/types";
import { getLocalStorageRunConfigurationKey } from "@app/velvet/helpers";
import {
  ChartTooltipButtonAttributePublishComment,
  ChartTooltipButtonAttributeRunConfigurationId
} from "@app/projects/network-assessment/ChartTooltipButton";
import { formatDateCustom } from "@app/shared/formatDateTime";
import { CountyProjectContainerState } from "@app/projects/CountyProjectContext";

/** Represents a point in cartesian coordinate space. */
class Point {
  constructor(
    /** The location of the point along the x-axis. */
    public x: number,
    /** The location of the point along the y-axis. */
    public y: number,
  ) { }
}

/** Represents a set of bounds for a given type. */
class Bounds<T> {
  constructor(
    /** The lower valued portion of the bounds. */
    public lower: T,
    /** The higher valued portion of the bounds. */
    public higher: T,
  ) { }
}

// ====================
// Constants
// ====================

/** The view box defining the bounds of the SVG. */
const viewBox = new Bounds(new Point(0, 0), new Point(450, 150));

/** Chart margins */
const margin = { bottom: 32, left: 20, right: 15, top: 25 };

/** The inner view box defined by adding margins to the SVG view box. */
const interiorViewBox = new Bounds(new Point(viewBox.lower.x + margin.left, viewBox.lower.y + margin.top), new Point(viewBox.higher.x - margin.right, viewBox.higher.y - margin.bottom));

/** Chart width */
const width = interiorViewBox.higher.x - interiorViewBox.lower.x;

/** Chart height */
const height = interiorViewBox.higher.y - interiorViewBox.lower.y;

/** Left and/or right padding of trend bars */
const barPadding = 0.6;

/** Scale gap values consistently */
let barYScale: d3.ScaleLinear<number, number>;

/** Scale index values consistently */
let lineYScale: d3.ScaleLinear<number, number>;

/** Scale x values consistently */
let xScale: d3.ScaleBand<number>;

/** Number of minor ticks on lineAxis */
let numMinorTicks: number;

/** Number of horizontal gridlines to show */
let numGridLines: number;

/** Width of lines for the unlicensed divider and text lines. */
const unlicensedLineStrokeWidth = 1;

/** Width of lines for Accuracy and Adequacy graphs */
const lineStrokeWidth = 2;

/** Width of lines for supplementary data/dual membership */
const secondaryLineStrokeWidth = 1;

/** Size of points at each Accuracy and Adequacy data point */
const lineDotRadius = 2.5;

/** The default time to take during a transition (ms). */
const transitionDuration = 750;

const defaultVelvetOptions: VelvetOptions = {
  fixedColumnWidths: true,
  showAdvancedComparison: false,
  showAttestation: true,
};

/** Default rendering colors for bars and lines */
const defaultColors: Colors = {
  BlueLine: VelvetColors.Blue,
  GreenLine: VelvetColors.Green,
  RedBar: VelvetColors.Red,
  YellowBar: VelvetColors.Yellow,
};

/** Gray scales of the default colors, used for an empty Trend Chart */
const grayColors: Colors = {
  BlueLine: "#1D1D1D",
  GreenLine: "#8a8a8a",
  RedBar: "#949494",
  YellowBar: "#9f9f9f",
};

const emptyVelvetItem: VelvetItem = {
  barGroups: [],
  disabled: false,
  itemType: null,
  linePoints: [],
  projectRunConfigurationId: null,
  publishComment: null,
  runFrequency: null,
  timestamp: undefined,
  tooltip: null,
};

/** An alias for a d3 selection. */
type RenderRegion = d3.Selection<d3.BaseType, unknown, HTMLElement | null, unknown | undefined>;

// ====================
// Render
// ====================

/**
 * Renders a Velvet into a given SVG element.
 * @param svg The element to render into.
 * @param config The configuration used to perform the render.
 */
function velvet(svg: SVGSVGElement, config?: VelvetConfiguration): void {
  let colors = defaultColors;
  let hasData = true;

  if (!config.options) {
    config.options = defaultVelvetOptions;
  }

  const legendTooltip = d3.select(".overview-trending-charts .chart")
    .append("div")
    .classed("d3-tooltip", true)
    .style("opacity", 0);

  const dataTooltip = d3.select(".overview-trending-charts .chart .velvet-tooltip")
    .style("opacity", 0);

  if (config.isDualMembership && (config.supplementaryItems?.length !== config.items?.length)) {
    return;
  }

  if (!config.items) {
    config = getMockConfig(config.isComparingResults, config.options);

    colors = grayColors;
    hasData = false;
  }

  // Adjust the values based off the bar group modes.
  for (const item of config.items) {
    item.barGroups = item.barGroups.map((bg) => applyModeValueTransform(bg));
  }

  const root = d3
    .select(svg)
    .attr("width", "100%") // We want to scale based off the width.
    .attr("viewBox", `${viewBox.lower.x} ${viewBox.lower.y} ${viewBox.higher.x} ${viewBox.higher.y}`);

  // Clear.
  root.selectAll("*").remove();

  // Add shadow definitions for text
  root.html(
    `<defs>
      <filter id="drop-shadow">
        <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
        <feOffset dx="0" dy="0" result="offsetblur"/>
        <feFlood flood-color="black"/>
        <feComposite in2="offsetblur" operator="in"/>
        <feMerge>
          <feMergeNode/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>`,
  );

  // Configure.
  if (config.isDualMembership && config.supplementaryItems) {
    setBounds(config.items.concat(config.supplementaryItems), config.use0To100YAxisBounds);
  } else {
    setBounds(config.items, config.use0To100YAxisBounds);
  }
  const renderItems = makeRenderItems(config, hasData, false);
  const supplementaryRenderItems = makeRenderItems(config, config.isDualMembership, true);

  // Render.
  const chartRoot = renderChartRoot(root);
  renderHorizontalGridLines(chartRoot.append("g"));
  renderBackgroundOutlines(chartRoot.append("g"), renderItems, supplementaryRenderItems, dataTooltip, config);
  renderBars(chartRoot.append("g"), renderItems, supplementaryRenderItems, config);
  if (!config.options.showAdvancedComparison) {
    renderTrendingUnlicensed(chartRoot.append("g"), grayColors);
    renderDividerLine(chartRoot.append("g"), grayColors, config.items.length);
  }
  renderLinesWithPoints(chartRoot.append("g"), renderItems, false);
  if (config.isDualMembership && supplementaryRenderItems) {
    renderLinesWithPoints(chartRoot.append("g"), supplementaryRenderItems, true);
  }
  renderXAxis(chartRoot.append("g"), renderItems);
  renderOutlines(chartRoot.append("g"), renderItems);
  renderIndexAxes(chartRoot.append("g"));
  renderGapAxes(chartRoot.append("g"));
  renderRadios(chartRoot.append("g"), renderItems);
  renderLegends(chartRoot, colors, legendTooltip, config);
}

const isEditable = (itemType: TrendChartItemType) => itemType !== TrendChartItemType.Projected && itemType !== TrendChartItemType.ProjectedTermed
  && itemType !== TrendChartItemType.Termed && itemType !== TrendChartItemType.Unpublished;

/**
 * Renders the root of the chart.
 * @param svgRoot The SVG root.
 */
function renderChartRoot(svgRoot: RenderRegion) {
  return svgRoot
    .append("g")
    .attr("transform", `translate(${margin.left}, ${margin.top})`);
}

/**
 * Examines the provided bar group and, if it's mode is "percentage" realigns the values to be a percentage
 * of their physical size.
 * @param barGroup The bar group to work with.
 */
function applyModeValueTransform(barGroup: VelvetBarGroup) {
  if (barGroup.mode !== "percentage") {
    return barGroup;
  }

  const sum = d3.sum(barGroup.items, (x) => x.value);
  if (sum === 0) {
    return barGroup;
  }

  for (const item of barGroup.items) {
    const percent = (item.value / sum) * 100;
    item.value = percent > 0 && percent < 0.01 ? 0.5 : percent; // 0.5 is the smallest visible section
  }

  return barGroup;
}

/**
 * Sets the boundaries of the chart based on the data we get
 * @param velvetItems
 */
function setBounds(velvetItems: VelvetItem[], use0To100YAxisBounds: boolean) {
  if (use0To100YAxisBounds) {
    numMinorTicks = 50;
    numGridLines = 10;

    barYScale = d3.scaleLinear<number>()
      .domain([0, 100])
      .range([height, 0]);

    lineYScale = d3.scaleLinear<number>()
      .domain([0, 100])
      .range([height, 0]);

    xScale = d3.scaleBand<number>()
      .paddingOuter(barPadding / 2)
      .paddingInner(barPadding)
      .range([0, width]);

    return;
  }

  const maxBar = velvetItems?.length > 0 ? d3.max(velvetItems, (d) => d3.max(d.barGroups, (e) => d3.sum(e.items, (f) => f.value))) : 0;

  let minLine = velvetItems?.length > 0 ? d3.min(velvetItems, (d) => d3.min(d.linePoints, (e) => e.value)) : 0;

  minLine = (minLine >= 75) ? 75 : minLine;
  numMinorTicks = 100 - minLine;

  if (numMinorTicks >= 50) {
    const roundedMinIndex = round10(minLine);

    minLine = roundedMinIndex > minLine
      ? roundedMinIndex - 10
      : roundedMinIndex;

    numMinorTicks = !!(numMinorTicks % 2)
      ? (numMinorTicks + 5) / 2
      : numMinorTicks / 2;
  } else {
    minLine = round5(minLine) >= 5
      ? round5(minLine) - 5
      : 0;
  }

  numGridLines = numMinorTicks / 5; // Show Grid line for every 5 ticks.

  // Determine the scaler to apply to the maxBar value based off the presence of percentage modes in bar groups.
  // If it is *only* percentage modes we use a value of 1.00.
  const maxBarScaler = velvetItems.some((item) => item.barGroups.some((bg) => bg.mode !== "percentage"))
    ? 1.25
    : 1.00;

  const maxDomain = Math.max(maxBar * maxBarScaler, 10); // bar legend goes from 0 to either 10 or maxBar count + 25%

  barYScale = d3.scaleLinear<number>()
    .domain([0, maxDomain])
    .range([height, 0]);

  lineYScale = d3.scaleLinear<number>()
    .domain([minLine, 100])
    .range([height, 0]);

  xScale = d3.scaleBand<number>()
    .paddingOuter(barPadding / 2)
    .paddingInner(barPadding)
    .range([0, width]);
}

function renderTrendingUnlicensed(root: RenderRegion, colors: Colors) {
  root
    .append("path")
    .classed("index-path", true)
    .attr("d", "M85 47 l30 0")
    .attr("stroke", colors.RedBar)
    .attr("stroke-width", unlicensedLineStrokeWidth)
    .attr("fill", "none");

  root
    .append("text")
    .text("Trending is not licensed")
    .attr("x", "120")
    .attr("y", "50")
    .attr("font-size", "50%")
    .attr("fill", colors.RedBar);

  root
    .append("path")
    .classed("index-path", true)
    .attr("d", "M205 47 l30 0")
    .attr("stroke", colors.RedBar)
    .attr("stroke-width", unlicensedLineStrokeWidth)
    .attr("fill", "none");
}

function renderDividerLine(root: RenderRegion, colors: Colors, itemCount: number) {
  const linePosition = 90.5 - 6.5 * itemCount;

  root
    .append("line")
    .classed("index-path", true)
    .attr("x1", `${linePosition}%`)
    .attr("x2", `${linePosition}%`)
    .attr("y1", "0%")
    .attr("y2", "62%")
    .attr("d", "M5 40 l215 0")
    .attr("stroke", colors.RedBar)
    .attr("stroke-width", unlicensedLineStrokeWidth)
    .attr("stroke-dasharray", "10,4")
    .attr("fill", "none");
}

/**
 * Renders the bars for Meets None and Meets One
 * @param root The root to render into.
 * @param renderItems The set of item information needed to render the bars.
 */
function renderBars(root: RenderRegion, renderItems: VelvetRenderItem[], supplementaryRenderItems: VelvetRenderItem[], { isDualMembership }: VelvetConfiguration) {
  // Get only properties we need for bar chart
  const dataIntermediate = renderItems.map<BarData>((item, index) => {
    return (item.barGroups || []).reduce<BarData>((result, v, i) => {
      if (isDualMembership && !!supplementaryRenderItems) {
        return {
          ...result,
          byGroupIndex: {
            ...result.byGroupIndex,
            [i]: {
              ...result.byGroupIndex[i],
              ...v.items.reduce((accumulator, interiorItem) => ({ ...accumulator, [interiorItem.color]: interiorItem.value }), {}),
            },
            [i + 1]: {
              ...result.byGroupIndex[i + 1],
              ...supplementaryRenderItems[index].barGroups[i].items.reduce((accumulator, interiorItem) => ({ ...accumulator, [interiorItem.color]: interiorItem.value }), {}),
            },
          },
          colorOrderByGroupIndex: {
            ...result.colorOrderByGroupIndex,
            [i]: v.colorOrder,
            [i + 1]: v.colorOrder,
          },
          disabled: item.disabled,
          unpublished: item.itemType === TrendChartItemType.Unpublished
        };
      }

      return {
        ...result,
        byGroupIndex: {
          ...result.byGroupIndex,
          [i]: {
            ...result.byGroupIndex[i],
            ...v.items.reduce((accumulator, interiorItem) => ({ ...accumulator, [interiorItem.color]: interiorItem.value }), {}),
          },
        },
        colorOrderByGroupIndex: {
          ...result.colorOrderByGroupIndex,
          [i]: v.colorOrder,
        },
        modeByGroupIndex: {
          ...result.modeByGroupIndex,
          [i]: v.mode,
        },
        tooltip: item.tooltip,
      };
    }, {
      byGroupIndex: {},
      colorOrderByGroupIndex: {},
      disabled: item.disabled,
      modeByGroupIndex: {},
      outlineId: item.outlineId,
      radioId: item.radioId,
      timestamp: item.timestampMonth,
      tooltip: item.tooltip,
      unpublished: item.itemType === TrendChartItemType.Unpublished
    });
  });

  const numGroups = d3.max(dataIntermediate, (d) => Object.keys(d.byGroupIndex).length);

  // Adjust xScale
  const x = xScale
    .domain(d3.range(dataIntermediate.length));

  // Create g (group) element for each series
  const g = root
    .selectAll("g")
    .data(d3.range(numGroups))
    .enter()
    .append("g")
    .selectAll(".chart > svg")
    .data((groupIndex) => {
      // Transpose data into layers, and make data ready for bar graph
      const keys = dataIntermediate.find((d) => !!d.colorOrderByGroupIndex[groupIndex]).colorOrderByGroupIndex[groupIndex] || [];
      const stackedData = d3.stack<BarData>()
        .keys(keys)
        .value((d, color) => (d.byGroupIndex[groupIndex] || {})[color] || 0)
        (dataIntermediate);
      return stackedData.map((series) => ({
        colorOrder: keys,
        groupIndex,
        series,
      }));
    })
    .enter()
    .append("g")
    .style("fill", (d, i) => {
      return (d.colorOrder || [])[i] || "transparent";
    });

  const unpublishedXPos = new Set<number>();
  const bars = g.selectAll("rect")
    .data((d) => d.series.map((v, i) => ({
      disabled: dataIntermediate[i].disabled,
      groupIndex: d.groupIndex,
      mode: dataIntermediate[i].modeByGroupIndex[d.groupIndex],
      outlineId: dataIntermediate[i].outlineId,
      radioId: dataIntermediate[i].radioId,
      tooltip: d.groupIndex === 0 ? dataIntermediate[i].tooltip : dataIntermediate[i].supplementaryTooltip,
      unpublished: dataIntermediate[i].unpublished,
      v,
    })))
    .enter()
    .append("rect")
    .classed("bar", true)
    .classed("disabled", (d) => d.disabled)
    .classed("js-unpublished", (d) => d.unpublished)
    .style("pointer-events", "none")
    .attr("width", x.bandwidth() / numGroups)
    .attr("height", 0)
    .attr("x", (d, i) => {
      const xVal = x(i) + (x.bandwidth() / numGroups) * d.groupIndex;
      if (d.unpublished) {
        unpublishedXPos.add(xVal);
      }
      return xVal;
    })
    .attr("y", (d) => barYScale(d.v[0]));

  // For each series create a rect element for each day
  let count = 0;
  const barTransitionDuration = transitionDuration / (renderItems.length * 2);

  bars
    .transition()
    .duration(transitionDuration)
    .delay(() => {
      const timing = count * barTransitionDuration;
      count++;
      return timing;
    })
    .attr("height", (d) => barYScale(d.v[0]) - barYScale(d.v[1]))
    .attr("y", (d) => barYScale(d.v[1]));


  bars
    .on("mouseover", (d) => {
      const disabled = d.disabled;

      d3.select(`#${d.outlineId}`)
        .classed("trend-item-active", !disabled);

      d3.select(`#${d.radioId}`)
        .classed("trend-item-active", !disabled);
    })
    .on("mouseout", (d) => {
      d3.select(`#${d.outlineId}`)
        .classed("trend-item-active", false);

      d3.select(`#${d.radioId}`)
        .classed("trend-item-active", false);
    });

  let unpublishedXPosArr = Array.from(unpublishedXPos);
  if (isDualMembership) {
    // we only need to render once per pair
    unpublishedXPosArr = unpublishedXPosArr.filter((xp, index) => index % 2 === 0);
  }
  unpublishedXPosArr.forEach((ux, index) => {
    // append to root because we only need one instance of each
    root.append("text")
      .text("Not Yet Published")
      .attr("class", "velvet-vertical-text")
      .attr("x", () => unpublishedXPosArr[index])
      .attr("filter", "url(#drop-shadow)")
      //.attr("transform", (d, i) => `rotate(90, ${unpublishedXPosArr[index] - 4}, 8)`) // top to bottom
      .attr("transform", () => `rotate(270, ${unpublishedXPosArr[index] + 45}, 36)`) // bottom to top
      .style("fill", "white")
      .style("font-size", "0.6rem")
      .style("font-weight", "bold")
      .style("opacity", "0.8")
      .style("pointer-events", "none");
    //  .style("font-size", "0.27rem") // vertical letters top to bottom (3 lines)
    //  .style("text-orientation", "upright")
    //  .style("writing-mode", "vertical-lr");
  });
}

/**
 * Renders outlines for the currently selected velvet items
 * @param root
 * @param renderItems
 */
function renderOutlines(root: RenderRegion, renderItems: VelvetRenderItem[]) {
  // Get only the data we need
  const outlinedData = renderItems.map((item) => {
    return {
      outlineClass: item.outlineClass,
      outlineId: item.outlineId,
    };
  });

  // Adjust xScale
  const x = xScale
    .domain(d3.range(renderItems.length));

  // Add outline
  root
    .classed("outlines", true)
    .selectAll("rect")
    .data(outlinedData)
    .enter()
    .append("rect")
    .attr("id", (d) => d.outlineId)
    .attr("class", (d) => d.outlineClass)
    .attr("height", height)
    .attr("width", x.bandwidth() * 1.4)
    .attr("y", 0)
    .attr("x", (_, i) => x(i) - (x.bandwidth() * 0.2));
}

/**
 * Renders the background outlines to catch and handle pointer events
 * @param root
 * @param renderItems
 */
function renderBackgroundOutlines(
  root: RenderRegion,
  renderItems: VelvetRenderItem[],
  supplementaryRenderItems: VelvetRenderItem[],
  tooltip: RenderRegion,
  config: VelvetConfiguration
) {
  // Get only the data we need
  const outlinedData = renderItems.map((item, index) => {
    return {
      hideEditCommentButton: !isEditable(item.itemType),
      outlineClass: item.outlineClass,
      outlineId: item.outlineId,
      projectRunConfigurationId: item.projectRunConfigurationId,
      publishComment: item.publishComment,
      supplementaryTooltip: config.isDualMembership ? supplementaryRenderItems[index].tooltip : undefined,
      tooltip: item.tooltip,
    };
  });

  // Adjust xScale
  const x = xScale
    .domain(d3.range(renderItems.length));

  // Add outline
  root
    .classed("outlines-bg", true)
    .selectAll("rect")
    .data(outlinedData)
    .enter()
    .append("rect")
    .attr("class", (d) => d.outlineClass)
    .attr("height", height)
    .attr("width", x.bandwidth() * 1.4)
    .attr("y", 0)
    .attr("x", (_, i) => x(i) - (x.bandwidth() * 0.2))
    .on("mouseover", (d, i) => {
      if (!config.isDualMembership) {
        showDataTooltip(tooltip, d, config.isDualMembership);
      }
      d3.select(`#${renderItems[i].outlineId}`)
        .classed("trend-item-active", !renderItems[i].disabled);

      d3.select(`#${renderItems[i].radioId}`)
        .classed("trend-item-active", !renderItems[i].disabled);
    })
    .on("mousemove", (d) => {
      if (config.isDualMembership) {
        showDataTooltip(tooltip, d, config.isDualMembership);
      }
    })
    .on("mouseout", (d, i) => {
      hideDataTooltip(tooltip);
      d3.select(`#${renderItems[i].outlineId}`)
        .classed("trend-item-active", false);

      d3.select(`#${renderItems[i].radioId}`)
        .classed("trend-item-active", false);
    });
}

/**
 * Renders the lines for Accuracy and Adequacy Indeces
 * @param root
 * @param renderItems
 */
function renderLinesWithPoints(root: RenderRegion, renderItems: VelvetRenderItem[], isDualMembership: boolean) {
  // Get only properties we need for line charts
  const dataIntermediate: LineData[] = renderItems.map((item) => {
    return (item.linePoints || []).reduce<LineData>((acc, v) => ({ ...acc, pointsByColor: { ...acc.pointsByColor, [v.color]: { value: v.value } } }), {
      disabled: item.disabled,
      outlineId: item.outlineId,
      pointsByColor: {},
      radioId: item.radioId,
      timestamp: item.timestampMonth
    });
  });

  const distinctColors = new Set<string>();
  for (const datum of dataIntermediate) {
    for (const color of Object.keys(datum.pointsByColor)) {
      distinctColors.add(color);
    }
  }

  // Adjust xScale
  const x = xScale
    .domain(d3.range(dataIntermediate.length));

  const colors = Array.from(distinctColors);
  const strokeWidth = isDualMembership ? secondaryLineStrokeWidth : lineStrokeWidth;

  root
    .selectAll("path")
    .data(colors)
    .enter()
    .append("path")
    .classed("index-path", true)
    .attr("d", (d) => getPathData(dataIntermediate, d))
    .attr("stroke", (d) => d)
    .attr("stroke-width", strokeWidth)
    .attr("stroke-linejoin", "round")
    .attr("fill", "none")
    .attr("stroke-dasharray", 1 + " " + 1)
    .attr("stroke-dashoffset", 1)
    .style("pointer-events", "none")
    .transition()
    .duration(transitionDuration)
    .attrTween("stroke-dasharray", tweenDash);

  root
    .selectAll("g")
    .data(colors)
    .enter()
    .selectAll("circle.adequacyIndex")
    .data((d) => dataIntermediate.map((intermediate) => ({
      ...intermediate,
      color: d,
      value: intermediate.pointsByColor[d]?.value,
    })))
    .enter()
    .append("circle")
    .attr("class", (d) => {
      return d.value === null || d.value === undefined
        ? "hidden"
        : null;
    })
    .attr("fill", (d) => d.color)
    .attr("cx", (_, i) => x(i) + (x.bandwidth() / 2))
    .attr("cy", (d) => lineYScale(d.value))
    .attr("r", lineDotRadius);
}

/**
 * Render Y-axis for accuracy and adequacy indexes
 * (left-hand side of the graph)
 * @param root
 */
function renderIndexAxes(root: RenderRegion) {
  root
    .attr("class", "y index-axis")
    .attr("margin-top", margin.top)
    .call(
      d3.axisLeft(lineYScale)
        .ticks(numMinorTicks)
        .tickSizeOuter(0)
        .tickSizeInner(-7),
    );
}

/**
 * Render Y-axis for "noneMetCount" and "oneMetCount" gap metrics
 * (right-hand side of graph)
 * @param root
 */
function renderGapAxes(root: RenderRegion) {
  root
    .attr("class", "y gap-axis")
    .attr("margin-top", margin.top)
    .attr("transform", `translate(${width})`)
    .call(
      d3.axisLeft(barYScale)
        .tickPadding(-13),
    );
}

/**
 * Render horizontal gridlines associated with the index axis
 * @param root
 */
function renderHorizontalGridLines(root: RenderRegion) {
  root
    .attr("class", "grid-horizontal")
    .call(
      d3.axisRight(lineYScale)
        .ticks(numGridLines)
        .tickSize(width)
        .tickSizeOuter(0),
    );
}

/**
 * Render the X-axis with labels
 * @param root
 * @param numItems
 */
function renderXAxis(root: RenderRegion, renderItems: VelvetRenderItem[]) {
  const xAxisText = root
    .attr("class", "x name-axis")
    .attr("transform", `translate(0, ${height})`)
    .call(
      d3.axisBottom(xScale)
        //.tickSizeOuter(0)
        .tickSize(0)
        .tickFormat((m, i) => {
          return renderItems.length && renderItems[i].xAxisLabel;
        }),
    )
    .selectAll("text");

  // neat underlines for the diagonal axis labels
  //root.selectAll("g")
  //  .append("line")
  //  .attr("x2", "55")
  //  .attr("y2", "2")
  //  .attr("pathLength", (d, i) => renderItems[i].xAxisLabel.length * 3)
  //  .attr("stroke", (d, i) => !!renderItems[i].xAxisLabel ? "rgb(102 102 102)" : "transparent")
  //  .attr("strokeWidth", "1px")
  //  .attr("transform", "rotate(-47 55 0) translate(-43.5 -35)");

  if (renderItems && renderItems.length > 0) {
    xAxisText
      .style("text-anchor", "end")
      .attr("class", "velvet-x-axis-label")
      .attr("dy", "1em")
      .attr("transform", "rotate(-45)")
      .attr("dx", "-2em")
      .attr("fill", (m, i) => renderItems[i].disabled ? grayColors.YellowBar : "currentcolor");
  }
}

/**
 * Renders legends for line and bar data.
 * Legends are rendered in reverse order from how they're passed in, grouped by lines and then bars.
 * @param root
 */
function renderLegends(root: RenderRegion, colors: Colors, tooltip: RenderRegion, config: VelvetConfiguration) {
  // We need to group the provided labels in the config according to their positioning and type.
  const groupings: { [O in "left" | "right"]: { [T in "line" | "bar"]: VelvetLabel[] } } = {
    left: {
      bar: [],
      line: [],
    },
    right: {
      bar: [],
      line: [],
    },
  };

  for (const label of config.labels) {
    groupings[label.orientation][label.type].push(label);
  }

  const legend = root.append("g").attr("class", "legend");
  const columnWidth = 100;
  const yValues = {
    circle: -8,
    rect: -9,
    text: -5.5,
  };

  // Maximum number of items placed vertically is two and as such is the cutoff before going to another column.
  // Lines are rendered before bars.

  /** Higher order function that creates a function that determines the x position of an element. */
  function xPos(manualShift = 0, columnShift = 0, direction = 1) {
    let breakOffset = 0;
    let position = 0 + (columnShift * 3);

    return (d: VelvetLabel) => {
      position += breakOffset;

      const res = Math.floor(position / 3) * (columnWidth * direction) + manualShift;

      // if the following label should be broken to a new column
      if (d.breakAfter) {
        breakOffset += 3 - Math.floor(position % 3);
      } else {
        breakOffset = 0;
        position += 1;
      }

      return res;
    };
  }

  /** Higher order function that creates a function that determines the y position of an element. */
  function yPos(manualShift = 0) {
    let position = 0;

    return (d: VelvetLabel) => {
      const res = (position % 3) * (-6) + manualShift;

      if (d.breakAfter) {
        position = 0;
      } else {
        position += 1;
      }

      return res;
    };
  }

  const leftLineEnter = legend
    .append("g")
    .selectAll("g")
    .data(groupings.left.line)
    .enter();

  leftLineEnter
    .append("rect")
    .attr("width", 14)
    .attr("height", 2)
    .attr("x", xPos())
    .attr("y", yPos(yValues.rect))
    .attr("fill", (d) => d.color);

  leftLineEnter
    .append("circle")
    .attr("cx", xPos(7))
    .attr("cy", yPos(yValues.circle))
    .attr("r", lineDotRadius)
    .attr("fill", (d) => d.color);

  leftLineEnter
    .append("text")
    .text((d) => d.name)
    .attr("x", xPos(18))
    .attr("y", yPos(yValues.text))
    .attr("font-size", 6)
    .attr("class", "velvet-legend");

  // The number of columns to shift based off the number of columns used for lines.
  let columnOffset = groupings.left.line.length > 0 ? Math.ceil(groupings.left.line.length / 3) : 0;

  const leftBarEnter = legend
    .append("g")
    .selectAll("g")
    .data(groupings.left.bar)
    .enter();

  leftBarEnter
    .append("rect")
    .attr("width", 5)
    .attr("height", 5)
    .attr("x", xPos(0, columnOffset))
    .attr("y", yPos(yValues.rect - 1.5))
    .attr("fill", (d) => d.color)
    // .attr("transform", `translate(${width + 5}, -23)`)
    .on("mouseover", (d) => {
      const elem = document.createElement("span");
      elem.textContent = d.tooltip;

      tooltip.transition().duration(200).style("opacity", 0.95);
      tooltip.html(`<span>${elem.innerHTML}</span>`)
        .style("left", `${d3.event.layerX + 15}px`)
        .style("top", `${(d3.event.layerY - 45)}px`);
    })
    .on("mouseout", () => {
      tooltip.transition().duration(500).style("opacity", 0);
    });

  leftBarEnter
    .append("text")
    .text((d) => d.name)
    .attr("x", xPos(7, columnOffset))
    .attr("y", yPos(yValues.text))
    .attr("font-size", 6)
    .attr("class", "velvet-legend");

  const rightLineEnter = legend
    .append("g")
    .selectAll("g")
    .data(groupings.right.line)
    .enter();

  rightLineEnter
    .append("rect")
    .attr("width", 14)
    .attr("height", 2)
    .attr("x", xPos(width - columnWidth, 0, -1))
    .attr("y", yPos(yValues.rect))
    .attr("fill", (d) => d.color);

  rightLineEnter
    .append("circle")
    .attr("cx", xPos(width - columnWidth + 7, 0, -1)) // Adjust
    .attr("cy", yPos(yValues.circle)) // Adjust
    .attr("r", lineDotRadius)
    .attr("fill", (d) => d.color);

  rightLineEnter
    .append("text")
    .text((d) => d.name)
    .attr("x", xPos(width - columnWidth + 18, 0, -1)) // Adjust
    .attr("y", yPos(yValues.text)) // Adjust
    .attr("font-size", 6)
    .attr("class", "velvet-legend");

  // The number of columns to shift based off the number of columns used for lines.
  columnOffset = groupings.right.line.length > 0 ? Math.ceil(groupings.right.line.length / 3) : 0;

  const rightBarEnter = legend
    .append("g")
    .selectAll("g")
    .data(groupings.right.bar)
    .enter();

  rightBarEnter
    .append("rect")
    .attr("width", 5)
    .attr("height", 5)
    .attr("x", xPos(width - columnWidth, columnOffset, -1))
    .attr("y", yPos(yValues.rect - 1.5))
    .attr("fill", (d) => d.color)
    // .attr("transform", `translate(${width + 5}, -23)`)
    .on("mouseover", (d) => {
      const elem = document.createElement("span");
      elem.textContent = d.tooltip;

      tooltip.transition().duration(200).style("opacity", 0.95);
      tooltip.html(`<span>${elem.innerHTML}</span>`)
        .style("left", `${d3.event.layerX + 15}px`)
        .style("top", `${(d3.event.layerY - 45)}px`);
    })
    .on("mouseout", () => {
      tooltip.transition().duration(500).style("opacity", 0);
    });

  rightBarEnter
    .append("text")
    .text((d) => d.name)
    .attr("x", xPos(width - columnWidth + 7, columnOffset, -1)) // Adjust
    .attr("y", yPos(yValues.text)) // Adjust
    .attr("font-size", 6)
    .attr("class", "velvet-legend");
}

/**
 * Renders the radio buttons (for events) under each month label
 * @param root
 */
function renderRadios(root: RenderRegion, renderItems: VelvetRenderItem[]) {
  root
    .attr("class", "radios")
    .attr("transform", `translate(0, ${height + 10})`)
    .call(
      d3.axisBottom(xScale)
        .tickSizeOuter(0)
        .tickSize(0)
        .tickFormat(() => {
          return "";
        }),
    );

  const ticks = root
    .selectAll(".radios .tick")
    .attr("class", (_, index) => {
      return renderItems[index].radioId === null
        ? "hidden"
        : null;
    })
    .style("font-family", "FontAwesome");

  // Render hover radios that also act as event catchers
  ticks
    .append("text")
    .attr("class", (_, index) => renderItems[index].radioClass)
    .classed("radio-bg", (_, index) => !renderItems[index].disabled)
    .attr("y", "3")
    .attr("dy", "-0.15em")
    .attr("dx", "0em")
    .attr("id", (_, index) => renderItems[index].radioId)
    .text((_, index) => renderItems[index].radioBgText)
    .on("click", function (data, index, groups) {
      return renderItems[index].radioFunction(this, data, index, groups);
    })
    .on("mouseover", (_, index) => {
      const isDisabled = renderItems[index].disabled;
      d3.select(`#${renderItems[index].outlineId}`)
        .classed("trend-item-active", !isDisabled);

      d3.select(`#${renderItems[index].radioId}`)
        .classed("trend-item-active", !isDisabled);
    })
    .on("mouseout", (_, index) => {
      d3.select(`#${renderItems[index].outlineId}`)
        .classed("trend-item-active", false);

      d3.select(`#${renderItems[index].radioId}`)
        .classed("trend-item-active", false);
    });

  // Render visible radios
  ticks
    .append("text")
    .attr("class", (_, index) => renderItems[index].radioClass)
    .classed("radio", (_, index) => !renderItems[index].disabled)
    .attr("y", "3")
    .attr("dy", "-0.50em")
    .text((_, index) => renderItems[index].radioText);
}

// ====================
// Helpers
// ====================

/**
 * Massages VelvetItems[] into a list of renderable items
 * @param velvetItems
 * @param hasData
 * @param isDualMembership: Whether we're returning primary or supplementary render items. Different from config.isDualMembership.
 */
function makeRenderItems(config: VelvetConfiguration, hasData: boolean, isDualMembership: boolean): VelvetRenderItem[] {
  const items: VelvetItem[] = fillWithEmpty(config, isDualMembership);
  if (!items || items.every((item) => !item || !item.timestamp)) {
    return;
  }
  const renderItems: VelvetRenderItem[] = items.map<VelvetRenderItem>((item) => {
    const isCompareRunSelected = !isDualMembership && config.isComparingResults && config.compareToRunConfigurationId === item.projectRunConfigurationId;
    const isProjectRunSelected = config.projectRunConfigurationId === item.projectRunConfigurationId;
    let radioClass: string;

    if (hasData && isProjectRunSelected) {
      if ((!config.isComparingResults || !!item.companionRunConfigurationId) && !item.disabled) {
        radioClass = "selected disabled";
      } else {
        radioClass = "disabled";
      }
    } else if (item.disabled && !isCompareRunSelected) {
      radioClass = "disabled";
    } else if (isCompareRunSelected && !item.disabled) {
      radioClass = "selected disabled";
    }

    return {
      ...item,
      hideEditCommentButton: !isEditable(item.itemType),
      outlineClass: (isProjectRunSelected || isCompareRunSelected) && !item.disabled
        ? "outline selected"
        : !item.projectRunConfigurationId
          ? null
          : "outline",
      outlineId: item.projectRunConfigurationId
        ? `outline-${item.projectRunConfigurationId}-${item.timestamp.valueOf()}`
        : null,
      publishComment: item.publishComment,
      radioBgText: "\uf10c",
      radioClass,
      radioFunction: (e: d3.BaseType) => {
        if (isProjectRunSelected || isCompareRunSelected || item.disabled) {
          return null;
        }

        toggleRadio(e);
        // we only want to store the primary run configuration id in local storage
        // route state is updated a bit further down in the updateRunConfigurationState calls, and that includes the compareToRunConfiguration
        if (!config.isComparingResults || config.isDualMembership) {
          const newStorageItem: LocalStorageRunConfigurationItem = {
            projectRunConfigurationId: item.projectRunConfigurationId,
            timeInserted: new Date().toISOString(),
          }
          localStorage.setItem(getLocalStorageRunConfigurationKey(config.projectId), JSON.stringify(newStorageItem));
        }

        const itemMonthConfigurations = items.filter((it) => it.timestamp && it.timestamp.month() === item.timestamp.month()).sort((a, b) => a.timestamp.diff(b.timestamp));
        const isLastOfMonth = itemMonthConfigurations[itemMonthConfigurations.length - 1].projectRunConfigurationId === item.projectRunConfigurationId || [TrendChartItemType.Projected, TrendChartItemType.Unpublished].includes(item.itemType);
        if (!isLastOfMonth && item.runFrequency === TimeFrequency.All) {
          toast("The selected configuration is not available in Monthly view, so your selection will be changed in that view.");
        }

        if (config.setRunConfiguration && config.updateRunConfigurationState) {
          if (config.isDualMembership) {
            // also need to make sure the second reactor renders
            const newSupplementaryConfigurationId = item.companionRunConfigurationId || undefined;
            let firstConfig: Partial<CountyProjectContainerState>;
            let secondConfig: Partial<CountyProjectContainerState>;
            config.setRunConfiguration(item.projectRunConfigurationId, item.timestamp, false, item.runFrequency, true).then((d) => {
              firstConfig = d;
              if (firstConfig && secondConfig) {
                config.updateRunConfigurationState({
                  ...firstConfig,
                  ...secondConfig,
                });
              }
            });
            config.setRunConfiguration(newSupplementaryConfigurationId, item.timestamp, true, item.runFrequency, true).then((d) => {
              secondConfig = d;
              if (firstConfig && secondConfig) {
                config.updateRunConfigurationState({
                  ...firstConfig,
                  ...secondConfig,
                });
              }
            });
          } else {
            config.setRunConfiguration(item.projectRunConfigurationId, item.timestamp, config.isComparingResults, item.runFrequency);
          }
        }
      },
      radioId: item.projectRunConfigurationId
        ? `radio-${item.projectRunConfigurationId}-${item.timestamp.valueOf()}`
        : null,
      radioText: (!hasData || item.projectRunConfigurationId &&
        (isProjectRunSelected || isCompareRunSelected)
        ? "\uf192"
        : item.projectRunConfigurationId
          ? "\uf10c"
          : null),
      timestampMonth: item.timestamp === undefined
        ? null
        : item.timestamp.month(),
      xAxisLabel: getXAxisLabel(item),
    };
  });

  return renderItems;
}

function getXAxisLabel(item: VelvetItem) {
  switch (item.itemType) {
    case TrendChartItemType.Projected:
      return "Projected";
    case TrendChartItemType.Termed:
      return "Termed";
    case TrendChartItemType.ProjectedTermed:
      return "Projected-Termed";
    case TrendChartItemType.OlderPublished:
    case TrendChartItemType.LatestPublished:
    case TrendChartItemType.Unpublished:
    default:
      switch (item.runFrequency) {
        case TimeFrequency.All:
          return item.timestamp === undefined ? "" : formatDateCustom(item.timestamp, "MMM DD, 'YY hh:mm A");
        case TimeFrequency.Monthly:
        default:
          return item.timestamp === undefined ? "" : item.timestamp.format("MMMM YYYY");
      }
  }
}

/**
 * Prepend the list of items if we want to use fixed column widths
 * @param config
 */
function fillWithEmpty(config: VelvetConfiguration, isDualMembership: boolean): VelvetItem[] {
  let items: VelvetItem[] = isDualMembership ? config.supplementaryItems : config.items;
  const itemsLength = config.items.length;
  if (config.options.fixedColumnWidths && itemsLength < 13) {
    let emptyCols: VelvetItem[] = [];

    for (let i = 0; i < 13 - itemsLength; i++) {
      emptyCols = emptyCols.concat(emptyVelvetItem);
    }

    items = emptyCols.concat(items);
  }
  return items;
}

/**
 * Round to nearest 5
 * @param num
 */
function round5(num: number): number {
  return Math.round(num / 5) * 5;
}

/**
 * Round to nearest 10
 * @param num
 */
function round10(num: number): number {
  return Math.round(num / 10) * 10;
}

/**
 * Get the (x, y) path data for drawing the line graph
 * @param trendType
 * @param lineData
 */
function getPathData(dataIntermediate: LineData[], color: string): string {
  // Adjust xScale
  const x = xScale
    .domain(d3.range(dataIntermediate.length));

  return d3.line<LineData>()
    .x((_, i) => x(i) + (x.bandwidth() / 2))
    .defined((d) => !(d.timestamp === undefined || d.timestamp === null || d.pointsByColor[color]?.value === undefined || d.pointsByColor[color]?.value === null)) // Don't draw lines for empty values
    .y((d) => lineYScale(d.pointsByColor[color]?.value))
    (dataIntermediate);
}

/**
 * Gets time duration for given points for the animation
 */
function tweenDash() {
  const totalLength = this.getTotalLength();
  const i = d3.interpolateString("0," + totalLength, totalLength + "," + totalLength);

  return (t: number) => i(t);
}

/**
 * Toggle the radios associated with trend groups
 * @param e
 * @param data
 * @param index
 * @param groups
 */
function toggleRadio(e: d3.BaseType) {
  const radio = d3.select(e);
  const isChecked = radio.text() === `\uf192` ? true : false;

  if (isChecked) {
    radio.text("\uf10c"); // fa-circle-o
  } else {
    radio.text("\uf192"); // fa-dot-circle-o
  }
}

function hideDataTooltip(tooltip: RenderRegion) {
  const tooltipElement = document.querySelector(".velvet-tooltip");
  const { clientX, clientY } = d3.event;
  const tooltipElementRect = tooltipElement.getBoundingClientRect();
  // Don't hide the tooltip if the user has moused from the chart bar to the tooltip
  if (tooltipElement && clientY > tooltipElementRect.top && clientY < tooltipElementRect.bottom
    && clientX > tooltipElementRect.left - 50 && clientX < tooltipElementRect.right + 50) {
    return;
  }
  tooltip.transition().duration(300).style("opacity", 0);
}

function showDataTooltip(tooltip: RenderRegion, d: { supplementaryTooltip: string, tooltip: string, hideEditCommentButton: boolean, projectRunConfigurationId: number, publishComment: string }, isDualMembership: boolean) {
  if (!d.tooltip) {
    return;
  }
  const tooltipElement = document.querySelector(".velvet-tooltip");
  const { clientX, clientY } = d3.event;
  const tooltipElementRect = tooltipElement.getBoundingClientRect();
  // Only show a new tooltip if the user has moused over a chart bar, not an open tooltip
  if (
    !isDualMembership &&
    tooltipElement &&
    window.getComputedStyle(tooltipElement).opacity !== "0" &&
    clientY > tooltipElementRect.top &&
    clientY < tooltipElementRect.bottom &&
    clientX > tooltipElementRect.left - 50
    && clientX < tooltipElementRect.right + 50) {
    return;
  }
  const chart = d3.event.target.closest("svg");
  const chartWidth = chart.scrollWidth;
  const isLeftHalfOfChart = d3.event.offsetX < chartWidth / 2;
  const chartBox = chart.getBoundingClientRect();
  const tooltipWidth = 250;
  const barBox = d3.event.target.getBoundingClientRect();
  const isLeftHalfOfBar = d3.event.clientX < barBox.left + (barBox.width / 2);
  const tooltipData = tooltip.select(".velvet-tooltip__data");
  if (isDualMembership && !isLeftHalfOfBar) {
    tooltipData.html(d.supplementaryTooltip);
  } else {
    tooltipData.html(d.tooltip);
  }
  if (isLeftHalfOfChart) {
    tooltip
      .style("left", `${barBox.right - chartBox.left + barBox.width + 24}px`)
      .classed("left", false);
  } else {
    tooltip
      .style("left", `${barBox.left - chartBox.left - tooltipWidth + 16}px`)
      .classed("left", true);
  }
  tooltip
    // 40% of the height of the chart, which includes all the date labels at the bottom
    .style("top", "40%")
    .style("transform", "translateY(-50%)");

  // Hide the edit project tags button for unpublished slices
  const buttonContainer = tooltip.select(".velvet-tooltip__button-container");
  buttonContainer.style("opacity", d.hideEditCommentButton ? "0" : "1")
    .style("pointer-events", d.hideEditCommentButton ? "none" : "auto");
  // Add the project run configuration id and publish comment to the button, so we can pull those details in the click event
  buttonContainer.select("button").attr(ChartTooltipButtonAttributeRunConfigurationId, d.projectRunConfigurationId);
  buttonContainer.select("button").attr(ChartTooltipButtonAttributePublishComment, d.publishComment);

  tooltip.transition().duration(150).style("opacity", "0.95");
}

/**
 * Creates and returns a mock config
 * @param isComparingResults
 */
function getMockConfig(isComparingResults: boolean, options: VelvetOptions): VelvetConfiguration {
  const mockConfig: VelvetConfiguration = {
    isComparingResults,
    items: d3.range(options.showAdvancedComparison ? -11 : -1, 2).map<VelvetItem>((x) => ({
      barGroups: [
        {
          colorOrder: [grayColors.RedBar, grayColors.YellowBar],
          items: [
            { color: grayColors.RedBar, value: random(0, 5, false) },
            { color: grayColors.YellowBar, value: random(0, 5, false) },
          ],
        },
      ],
      disabled: false,
      itemType: TrendChartItemType.OlderPublished,
      linePoints: [
        { color: grayColors.GreenLine, value: random(70, 100, false) },
        { color: grayColors.BlueLine, value: random(70, 100, false) },
      ],
      projectRunConfigurationId: null,
      publishComment: null,
      runFrequency: TimeFrequency.Monthly,
      timestamp: moment().add(x, "month"),
      tooltip: null,
    })),
    labels: [],
    module: "Adequacy",
    options,
    projectId: null,
    projectRunConfigurationId: null,
    projectRunConfigurationTimestamp: null,
    setRunConfiguration: null,
    supplementaryItems: null,
    updateRunConfigurationState: null,
    use0To100YAxisBounds: null,
  };

  return mockConfig;
}

// Exports
export default velvet;
export {
  type VelvetConfiguration,
  type VelvetOptions,
};
